import { Badge, Button, CircularProgress, Switch, Tab, Tabs, TextField, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
    DataGrid,
    FilterPanelTrigger,
    getGridStringOperators,
    GridFilterListIcon,
    GridFilterPanel,
    QuickFilter,
    QuickFilterControl,
    Toolbar,
    ToolbarButton,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BSSelect from "../../../common/js/components/BSSelect";
import { Search } from "../../../common/js/icons";
import operations from "./ducks/operations";

const StyledPageToolbar = styled("div")(() => ({
    marginBottom: "20px",
    marginTop: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
}));

const StyledTabOne = styled(Tab)(() => ({
    marginRight: "20px",
}));

const StyledLoadingSpinner = styled("div")(() => ({
    position: "absolute",
    left: "50%",
    top: "45%",
    transform: "translate(-50%, -50%)",
    marginTop: "50px",
}));

const StyledLink = styled(Link)(() => ({
    textDecoration: "none",
}));

const StyledText = styled("span")(() => ({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
}));

const StyledFilterPanel = (props) => (
    <GridFilterPanel
        {...props}
        sx={{
            "& .MuiFormLabel-root.MuiInputLabel-root": {
                top: "-15px ",
            },
            "& .MuiInputBase-root.MuiOutlinedInput-root:not(.MuiSelect-root)": {
                border: "none ",
            },
        }}
    />
);

const DocumentAdminDatagridToolbar = () => {
    return (
        <Toolbar sx={{ gap: "1vw" }}>
            <Tooltip title="Parent Type Filters">
                <FilterPanelTrigger
                    render={(props, state) => (
                        <ToolbarButton {...props} color="default">
                            <Badge badgeContent={state.filterCount} color="primary" variant="dot">
                                <GridFilterListIcon fontSize="small" />
                            </Badge>
                        </ToolbarButton>
                    )}
                />
            </Tooltip>
            <QuickFilter defaultExpanded>
                <QuickFilterControl
                    render={({ ref, ...controlProps }, state) => (
                        <TextField
                            {...controlProps}
                            ownerState={{ expanded: state.expanded }}
                            placeholder="Search Documents"
                            inputRef={ref}
                            size="small"
                            slotProps={{
                                input: {
                                    startAdornment: <Search />,
                                },
                            }}
                        ></TextField>
                    )}
                />
            </QuickFilter>
        </Toolbar>
    );
};

const DocumentAdminLanding = (props) => {
    const { handleSave } = props;
    const [tab, setTab] = useState("documents");
    const [documentTypes, setDocumentTypes] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [parentTypes, setParentTypes] = useState([]);

    const documentColumns = [
        {
            field: "name",
            headerName: "NAME",
            flex: 1,
            disableColumnMenu: true,
            filterable: false,
            renderCell: (params) => {
                return (
                    <StyledLink to={params.row.link}>
                        <StyledText>{params.row.name}</StyledText>
                    </StyledLink>
                );
            },
        },
        {
            field: "description",
            headerName: "DESCRIPTION",
            flex: 1,
            filterable: false,
            disableColumnMenu: true,
        },
        {
            field: "parentType",
            headerName: "PARENT TYPE",
            flex: 1,
            disableColumnMenu: true,
            filterOperators: getGridStringOperators()
                .filter((operator) => operator.value === "equals")
                .map((operator) => ({
                    ...operator,
                    InputComponent: ({ item, applyValue }) => {
                        const parentTypeOptions = [
                            ...Array.from(
                                new Set(documentTypes.map((doc) => doc.parentEntityType?.name).filter(Boolean))
                            ).map((option) => ({ value: option, label: option })),
                        ];

                        return (
                            <BSSelect
                                id="parentType-filter"
                                label="Value"
                                value={item.value || "COMP"}
                                options={parentTypeOptions}
                                onChange={(e) => applyValue({ ...item, value: e.target.value })}
                                extraInputStyles={{ "--bsselect-input-mt": "1px" }}
                            />
                        );
                    },
                })),
        },
    ];

    const documentRows = Object.keys(documentTypes).map((documentType, index) => {
        return {
            name: documentTypes[documentType]?.name,
            id: index,
            description: documentTypes[documentType]?.description,
            parentType: documentTypes[documentType]?.parentEntityType.name,
            link: "/document-type/document/" + documentTypes[documentType]?.documentTypeKey,
        };
    });

    const parentColumns = [
        {
            field: "name",
            headerName: "NAME",
            flex: 1,
            disableColumnMenu: true,
            renderCell: (params) => {
                return (
                    <StyledLink to={params.row.link}>
                        <StyledText>{params.row.name}</StyledText>
                    </StyledLink>
                );
            },
        },
        {
            field: "proxyToggle",
            headerName: "Permit Proxy Attachment Access",
            flex: 1,
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params) => {
                return <Switch checked={params.row.proxyFlg} onChange={() => handleFlagToggle(params.id)} />;
            },
        },
    ];

    const parentRows = parentTypes.map((parentType, index) => {
        return {
            id: index,
            name: parentType.name,
            proxyFlg: parentType.proxyFlg,
            link: "/parent-type/" + parentType.parentEntityTypeKey,
        };
    });

    const handleFlagToggle = (id) => {
        const updatedParentTypes = parentTypes.map((parentType, index) => {
            if (index === id) {
                return { ...parentType, proxyFlg: !parentType.proxyFlg };
            }
            return parentType;
        });

        setParentTypes(updatedParentTypes);
        handleSave([updatedParentTypes[id]]);
    };

    const loadData = async () => {
        const docTypes = await operations.getAllDocumentTypes();
        const sorted = [...docTypes].sort((a, b) => ("" + a.name).localeCompare(b.name));
        setDocumentTypes(sorted);
        setDataLoaded(true);
    };

    const switchTab = async (event, value) => {
        setTab(value);

        if (value === "parentTypes" && Object.keys(parentTypes).length === 0) {
            setDataLoaded(false);
            const currentParentTypes = await operations.getAllParentEntityTypes();
            setParentTypes(currentParentTypes);
        }
        setDataLoaded(true);
    };

    useEffect(() => {
        loadData();
    }, []);

    return (
        <div>
            <StyledPageToolbar>
                <h2 className="page-title">Document Admin</h2>
                {tab === "parentTypes" ? null : (
                    <Button disabled={false} variant="contained" color="primary" href="/document-type/create">
                        + Add Document
                    </Button>
                )}
            </StyledPageToolbar>
            <div className="simPaper">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Tabs value={tab} onChange={switchTab}>
                        <StyledTabOne value="documents" label="Documents" />
                        <Tab value="parentTypes" label="Parent Types" />
                    </Tabs>
                </div>

                <div style={{ minHeight: "60vh" }}>
                    {dataLoaded ? (
                        <div style={{ display: "flex", width: "100%", height: "100%" }}>
                            {tab === "documents" ? (
                                <div style={{ display: "flex", width: "100%", height: "100%" }}>
                                    <DataGrid
                                        columns={documentColumns}
                                        rows={documentRows}
                                        hideFooter={true}
                                        slots={{
                                            toolbar: DocumentAdminDatagridToolbar,
                                            filterPanel: StyledFilterPanel,
                                        }}
                                        showToolbar={true}
                                        initialState={{
                                            filter: {
                                                filterModel: {
                                                    items: [
                                                        {
                                                            field: "parentType",
                                                            operator: "equals",
                                                            value: "COMP",
                                                        },
                                                    ],
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            ) : (
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "1vw",
                                        flexDirection: "column",
                                        margin: "1vw",
                                        flex: "1",
                                    }}
                                >
                                    <DataGrid columns={parentColumns} rows={parentRows} hideFooter={true} />
                                </div>
                            )}
                        </div>
                    ) : (
                        <StyledLoadingSpinner>
                            <CircularProgress color="primary" />
                        </StyledLoadingSpinner>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DocumentAdminLanding;
